import { SectionCard } from '@atlas/ui-components'
import { NOT_YET_TAGGED } from '@/domain/asset-detail.types'

export interface AttributesPanelProps {
	licence: string | null
	checksum: string | null
	fileSize: number | null
	/** Persisted generic `object_attribute` values (e.g. `campaign`), keyed by axis — rendered after the fixed rows above. */
	genericAttributes?: { axisCode: string; label: string; value: string }[]
}

function formatFileSize(bytes: number | null): string {
	if (bytes === null) return '—'
	if (bytes < 1024) return `${bytes} B`
	if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
	return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
	return (
		<div className="flex items-center justify-between py-1 text-sm">
			<dt className="text-ds-text-muted">{label}</dt>
			<dd className="font-medium text-ds-text">{children}</dd>
		</div>
	)
}

/**
 * Real attributes panel: licence, checksum,
 * and file size, all derived from the file's real classification tags and metadata.
 * A missing attribute renders an explicit "not yet tagged" placeholder rather than a
 * blank field — real demo data is not seeded on these axes today. Presentational + storied.
 */
export function AttributesPanel({ licence, checksum, fileSize, genericAttributes = [] }: AttributesPanelProps) {
	return (
		<SectionCard title="Attributes" className="space-y-1 bg-ds-surface">
			<dl>
				<Row label="Licence">
					<span className={licence ? 'text-ds-success' : 'text-ds-text-subtle'}>{licence ?? NOT_YET_TAGGED}</span>
				</Row>
				<Row label="SHA-256">
					<span className="font-mono text-xs">{checksum ? `${checksum.slice(0, 12)}…` : '—'}</span>
				</Row>
				<Row label="File size">{formatFileSize(fileSize)}</Row>
				{genericAttributes.map((attribute) => (
					<Row key={attribute.axisCode} label={attribute.label}>
						{attribute.value}
					</Row>
				))}
			</dl>
		</SectionCard>
	)
}
